export interface StatusBadgeProps {
  readonly label: string;
  /**
   * `kiosk` is the component's `Primary` variant, the chip every candidate screen carries. `rep` is its
   * `Warning` variant, which the frame instances on `04 · See a rep` — the screen where a person, not the
   * machine, takes over.
   */
  readonly tone?: 'kiosk' | 'rep';
}

/**
 * The small dot-and-label chip in a card's header
 * [Figma · component `Status Badge` — 11876:19750 Primary · 11876:19756 Warning, read live 2026-09-08].
 *
 * **Three colours per variant, and the boundary is one of them.** Each variant is a surface, a 1px border
 * and one ink used for BOTH the dot and the label. This build had none of that right: it borrowed the
 * callout's surfaces (`--primary-muted` / `--warning-muted`, each a shade lighter than the chip's own),
 * painted the primary dot `--primary` teal where the component paints it the same #026776 as the label,
 * and drew no border at all — which is what made the chip read as a tint rather than as a chip on all six
 * screens that carry one.
 *
 * The 0.18px of tracking IS the component's value: Figma reports the chip style's letter-spacing as `1.5`,
 * which is 1.5% of the 12px size.
 *
 * An INSET RING rather than a border, for the reason `Field` and `YesNo` carry the same note: Figma aligns
 * a frame's stroke INSIDE, so the component measures 60 x 20 with its 1px boundary already inside the 16px
 * label and 2px of padding. A CSS border is added to an auto height, which would have made every chip 22
 * tall and pushed the header row it is centred in out of step with the frame's 36.
 */
export function StatusBadge({ label, tone = 'kiosk' }: StatusBadgeProps) {
  const rep = tone === 'rep';
  return (
    <span
      /*
        A hook, for the same reason `Field` carries `data-affix` and `YesNo` carries `data-yesno`:
        `KioskCard` has to right-align a header that holds ONLY this chip, and the accessibility tree
        gives it no way to tell this span from the Back control beside it. Card-level styling keyed on
        child count alone threw check-in's lone Back to the right edge.
      */
      data-badge=""
      className={`flex items-center gap-1 rounded-[var(--radius-full)] px-2 py-[2px] ring-1 ring-inset ${
        rep
          ? 'bg-[var(--badge-warning-surface)] ring-[var(--badge-warning-border)]'
          : 'bg-[var(--badge-surface)] ring-[var(--badge-border)]'
      }`}
    >
      <span
        className={`size-[6px] rounded-[var(--radius-full)] ${rep ? 'bg-[var(--badge-warning-foreground)]' : 'bg-[var(--badge-foreground)]'}`}
      />
      <span
        className={`text-xs font-semibold tracking-[0.18px] ${rep ? 'text-[var(--badge-warning-foreground)]' : 'text-[var(--badge-foreground)]'}`}
      >
        {label}
      </span>
    </span>
  );
}
